/**
 * TTS 配置管理 Composable
 * 提取 TextToAudio.vue 中的配置相关逻辑
 */

import { ref, computed, reactive, watch } from 'vue';
import { useTTSStore, useConfigStore, useAudioStore } from '@/store';
import { useEntitlementStore } from '@/store/entitlement';
import { storeToRefs } from 'pinia';
import { API_OPTIONS } from '@/utils/app-runtime';

export function useTTSConfiguration() {
  const ttsStore = useTTSStore();
  const configStore = useConfigStore();
  const audioStore = useAudioStore();
  const entitlementStore = useEntitlementStore();

  // 响应式引用Store数据
  const { inputs, formConfig, page } = storeToRefs(ttsStore);

  // 播放器配置
  const playerConfig = reactive({
    formatType: 'mp3',
    autoplay: true
  });

  // 语音列表
  const voiceSelectList = ref<any[]>([]);

  // API选项配置 - 使用统一常量
  const apiOptions = API_OPTIONS;

  // 语言选项 - 使用固定配置（后端API会根据type参数筛选）
  const languageOptions = computed(() => {
    // 现在使用后端API，这里只需要提供基本的语言选项
    return [
      { label: '中文', value: 'zh-CN' },
      { label: 'English', value: 'en-US' },
      { label: 'Español', value: 'es-MX' },
      { label: '日本語', value: 'ja-JP' }
    ];
  });

  // 语音选项 - 根据当前语音列表生成
  const voiceOptions = computed(() => {
    if (!voiceSelectList.value || voiceSelectList.value.length === 0) {
      return [];
    }

    return voiceSelectList.value.map((voice: any) => ({
      label: voice.localName || voice.name,
      value: voice.name,
      gender: voice.gender,
      locale: voice.locale
    }));
  });

  // 格式选项
  const formatOptions = [
    { label: 'MP3', value: 'mp3' },
    { label: 'WAV', value: 'wav' }
  ];

  // 计算属性 - 可写的计算属性
  const isSSMLMode = computed({
    get: () => ttsStore.isSSMLMode,
    set: (value: boolean) => {
      ttsStore.page.tabIndex = value ? '1' : '0';
    }
  });

  // 其他计算属性
  const getMaxCharCount = computed(() => {
    const api = ttsStore.formConfig?.api;

    if (api === 5) {
      // 免费TTS服务 - 使用 entitlementStore 的配额限制
      return entitlementStore.quota.total || 5000;
    }

    // 其他API的字符限制
    const limits: Record<number, number> = {
      1: 10000, // Microsoft Speech API
      2: 5000,  // Edge Speech API
      3: 10000, // Azure Speech API
      4: 8000   // TTS88 API
    };

    return limits[api as number] || 5000;
  });

  const getCurrentQuotaUsed = computed(() => {
    // 统一使用 entitlementStore 的配额信息
    return entitlementStore.quota.used || 0;
  });

  const getCurrentQuotaLimit = computed(() => {
    // 统一使用 entitlementStore 的配额信息
    return entitlementStore.quota.total || 5000;
  });

  const getQuotaTitle = computed(() => {
    // 根据用户层级返回标题
    return entitlementStore.tierLabel || '免费额度';
  });

  const canConvert = computed(() => {
    const inputContent = ttsStore.isSSMLMode
      ? ttsStore.inputs.ssmlValue
      : ttsStore.inputs.inputValue;

    // 只检查是否有输入内容，不检查额度
    // 额度不足时在点击转换按钮后显示友好提示
    if (!inputContent || inputContent.trim() === '') {
      return false;
    }

    return true;
  });

  const getError = computed(() => {
    return ttsStore.error;
  });

  const shouldShowErrorDisplay = computed(() => {
    return ttsStore.error !== null;
  });

  const getAudioSrc = computed(() => {
    return audioStore.currentAudioUrl || '';
  });

  const getAudioInfo = computed(() => {
    return audioStore.currentAudioInfo;
  });

  const shouldShowFirstTimeHint = computed(() => {
    return !configStore.config.hasShownFirstTimeHint;
  });

  const shouldShowComparisonTable = computed(() => {
    return configStore.config.showComparisonTable;
  });

  /**
   * 初始化配置
   */
  const initializeConfiguration = async () => {
    try {
      // 初始化语音列表
      await updateVoiceList();
      
      // 设置默认格式
      if (!playerConfig.formatType) {
        playerConfig.formatType = 'mp3';
      }
      
      console.log('配置初始化完成');
    } catch (error) {
      console.error('配置初始化失败:', error);
    }
  };

  /**
   * 更新语音列表 - 使用统一的数据初始化管理器
   */
  const updateVoiceList = async () => {
    try {
      // 使用统一的数据初始化管理器
      const { useDataInitializer } = await import('@/composables/useDataInitializer');
      const { initVoiceData } = useDataInitializer();

      // 使用新的 useVoiceStore 获取语音数据
      const { useVoiceStore } = await import('@/store');
      const voiceStore = useVoiceStore();

      // 使用统一初始化，避免重复请求
      await initVoiceData();

      // 将语音数据转换为选择列表格式
      voiceSelectList.value = voiceStore.voices.map((voice: any) => ({
        value: voice.shortName,
        label: voice.displayName,
        localName: voice.localName,
        gender: voice.gender,
        language: voice.language?.name || '',
        locale: voice.locale
      }));

      console.log('语音列表更新成功，共', voiceSelectList.value.length, '个语音');
    } catch (error) {
      console.error('更新语音列表失败:', error);
    }
  };

  /**
   * 设置格式类型
   */
  const setFormatType = (format: string) => {
    playerConfig.formatType = format;
    ttsStore.formConfig.formatType = format;
  };

  // 监听语言变化，自动更新语音列表
  watch(
    () => ttsStore.formConfig?.languageSelect,
    (newLanguage) => {
      if (newLanguage) {
        updateVoiceList();
      }
    }
  );

  return {
    // Store 数据 (响应式)
    inputs,
    formConfig,
    page,
    
    // 配置状态
    playerConfig,
    voiceSelectList,
    
    // 选项配置
    apiOptions,
    languageOptions,
    voiceOptions,
    formatOptions,
    
    // 计算属性
    isSSMLMode,
    getMaxCharCount,
    getCurrentQuotaUsed,
    getCurrentQuotaLimit,
    getQuotaTitle,
    canConvert,
    getError,
    shouldShowErrorDisplay,
    getAudioSrc,
    getAudioInfo,
    shouldShowFirstTimeHint,
    shouldShowComparisonTable,

    // 方法
    initializeConfiguration,
    updateVoiceList,
    setFormatType,
  };
}
